import { Form, Formik } from "formik";
import * as React from "react";
import * as Yup from "yup";
import { t } from "../../../i18n/util";
import { JobCategory, JobCategoryCreatePayload, JobCategoryUpdatePayload } from "../../../network/APITypes";
import { useUsers } from "../../hooks/useUsers";
import { DialogHeader, ErrorMessagesList, FormButtons, HrBotDialog } from "../../ui/Primitives";
import { THEME } from "../../../theme/ThemeStore";
import { JobCategoryEditDetailsGrid } from "../JobCategoryEditDetailsGrid";
import { optionToString } from "../../util/helpers";
import { getJobCategoryOptions } from "../../../constants";

type Props = {
    initialValues?: Partial<JobCategory>;
    onSubmit: (values: JobCategoryCreatePayload | JobCategoryUpdatePayload) => Promise<void>;
    onBack?: () => void;
    isOpen: boolean;
    isEdit?: boolean;
};

const getInitialValues = (jobCategory?: Partial<JobCategory>, users?: any[]): JobCategoryCreatePayload => {
    const notificationUserEmails = jobCategory?.notificationUserEmails || [];
    const primaryContactEmails = jobCategory?.primaryContactEmails || [];

    return {
        name: optionToString(getJobCategoryOptions(), jobCategory?.name) || jobCategory?.name || "",
        isActive: jobCategory?.isActive ?? true,
        isDal: jobCategory?.isDal ?? (THEME.THEME_NAME === "dal"),
        notificationUserEmails,
        primaryContactEmails
    };
};

export const JobCategoryEditDialog = ({ initialValues, onSubmit, onBack, isOpen, isEdit = false }: Props) => {
    const [open, setOpen] = React.useState(isOpen);
    const users = useUsers() || [];

    React.useEffect(() => {
        setOpen(isOpen);
    }, [isOpen]);

    const handleClose = () => {
        setOpen(false);
        onBack?.();
    };

    const handleSubmit = (values: JobCategoryCreatePayload) => {
        setOpen(false);

        if (isEdit && initialValues?.id) {
            const updatePayload: JobCategoryUpdatePayload = {};
            if (values.name !== initialValues.name) updatePayload.name = values.name;
            if (values.isActive !== initialValues.isActive) updatePayload.isActive = values.isActive;
            if (values.notificationUserEmails.length > 0) updatePayload.notificationUserEmails = values.notificationUserEmails;
            if (values.primaryContactEmails.length > 0) updatePayload.primaryContactEmails = values.primaryContactEmails;

            onSubmit(updatePayload);
        } else {
            onSubmit(values);
        }
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().required(t("error.required")),
        notificationUserEmails: Yup.array()
            .min(1, t("error.atLeastOneContact"))
            .required(t("error.required")),
        primaryContactEmails: Yup.array()
            .min(1, t("error.atLeastOnePrimaryContact"))
            .required(t("error.required"))
    });

    const formInitialValues = React.useMemo(() => {
        if (isEdit && !initialValues) {
            return getInitialValues(undefined, users);
        }
        return getInitialValues(initialValues, users);
    }, [initialValues, users, isEdit]);

    if (isEdit && !initialValues) {
        return null;
    }

    return (
        <HrBotDialog open={open} maxWidth={false}>
            <div style={{
                width: 800,
                height: 600,
                maxHeight: '80vh',
                overflow: 'auto'
            }}>
                <DialogHeader
                    label={isEdit ? t("screen.applicantDetails.edit") : t("screen.jobcategories.add")}
                    onClose={handleClose}
                />
                <Formik
                    initialValues={formInitialValues}
                    onSubmit={handleSubmit}
                    validationSchema={validationSchema}
                    validateOnChange
                    validateOnBlur
                    validateOnMount
                    enableReinitialize={true}
                >
                    {({ errors, touched, setFieldValue, values }) => {
                        return (
                            <Form>
                                <div style={{ padding: 24 }}>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <JobCategoryEditDetailsGrid values={values} setFieldValue={setFieldValue} />

                                        <ErrorMessagesList errors={errors} touched={touched} style={{ marginTop: 20 }} />
                                        <FormButtons
                                            labelBack={t("screen.dataUpload.dialog.cancel")}
                                            labelSubmit={isEdit ? t("screen.applicantDetails.save") : t("screen.applicantDetails.save")}
                                            onBack={handleClose}
                                            style={{
                                                justifyContent: "flex-end",
                                                marginBottom: 0,
                                                marginTop: 20
                                            }}
                                        />
                                    </div>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </HrBotDialog>
    );
};